import { callApi } from './api';
import Category, { CategoryType } from '@/models/category';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const categoriesApiUrl = import.meta.env.VITE_API_URL_V1 + '/categories';

// Scope for the category list. Omitted = content scope (the App discovery filter
// — unchanged behaviour). 'course' = course categories (course picker). 'blog' =
// blog categories (Blog admin dropdown). 'all' = every category, unscoped (the
// Categories admin manager). Maps to the API's ?type= param.
export type CategoryScope = CategoryType | 'all';

export const getCategories = async (scope?: CategoryScope) => {
    return callApi<Category[]>({
        url: scope ? `${categoriesApiUrl}?type=${scope}` : `${categoriesApiUrl}`,
        method: 'GET',
    });
};
export const useCategories = (scope?: CategoryScope) => {
    return useQuery({
        // Keep the no-arg key as ['categories'] so existing consumers' caches are
        // untouched; scoped lists get their own key.
        queryKey: scope ? ['categories', scope] : ['categories'],
        queryFn: () => getCategories(scope),
    });
};

export const getCategory = async (categoryId: string) => {
    return callApi<Category>({
        url: `${categoriesApiUrl}/${categoryId}`,
        method: 'GET',
    });
};
export const useCategory = (categoryId: string, isOpen: boolean) => {
    return useQuery({
        queryKey: ['category', categoryId],
        queryFn: () => getCategory(categoryId),
        enabled: !!categoryId && isOpen,
    });
};

export const createCategory = async ({ name, type, hidden }: { name: string; type: CategoryType; hidden?: boolean }) => {
    return callApi<Category>({
        url: `${categoriesApiUrl}`,
        method: 'POST',
        data: { name, type, hidden },
    });
};
export const useCreateCategory = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createCategory,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['categories'] });
        },
    });
};

export const updateCategory = async ({
    categoryId,
    name,
    type,
    hidden,
}: {
    categoryId: string;
    name: string;
    type: CategoryType;
    hidden?: boolean;
}) => {
    return callApi({
        url: `${categoriesApiUrl}/${categoryId}`,
        method: 'PUT',
        data: { name, type, hidden },
    });
};
export const useUpdateCategory = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateCategory,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['categories'] });
        },
    });
};

export const deleteCategory = async ({ categoryId }: { categoryId: string }) => {
    return callApi({
        url: `${categoriesApiUrl}/${categoryId}`,
        method: 'DELETE',
    });
};
export const useDeleteCategory = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteCategory,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['categories'] });
        },
    });
};
